import React from 'react';
import { useDispatch } from 'react-redux';
import { GridActionsCellItem, useGridApiContext, type GridRowParams } from '@mui/x-data-grid-premium';

import { type PaginatorType } from 'app/main/components/hooks/usePaginator';
import { type MultiButtonOptionType } from 'app/main/components/buttons/MultiButton';
import { type ViewItemButtonProps } from 'app/main/components/page-layouts/view/ViewPage';
import { Icon, Utils, useSettings, useUserData } from 'app/main/components';
import { DetailsAdditionalPropsContext, ViewTableUtils } from 'app/main/components/page-layouts';
import { ViewTableRowActionsMenuComponent } from 'app/main/components/page-layouts/view/table/ViewTableComponents';
import { showOrdersLinesEditDialog } from './OrdersLinesEditDialog';

interface OrdersLinesTableActionsProps {
	rowProps: GridRowParams<any>;
	order: any;
	paginatorProps: PaginatorType;
	updateRefresh: VoidFunction;
}

export default function OrdersLinesTableActions({
	rowProps,
	order,
	paginatorProps,
	updateRefresh
}: OrdersLinesTableActionsProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('orders_lines');
	const userData = useUserData();
	const apiRef = useGridApiContext();

	const { nPage, nRows, maxRows } = paginatorProps;
	const rowIndex = (nPage - 1) * nRows + apiRef.current.getSortedRowIds().indexOf(rowProps.id);

	const { additionalProps } = React.useContext<any>(DetailsAdditionalPropsContext);

	function updateOrder(newOrder?: number) {
		Utils.executeStored({
			action: 'st_orders_lines_update',
			dispatch,
			data: {
				orli_uid: rowProps.row.orli_uid,
				orli_ords_uid: rowProps.row.orli_ords_uid,
				orli_user_modified_uid: userData.user_uid,
				orli_order: newOrder
			},
			successHandler: _response => {
				dispatch(Utils.alertLocalizedMsg(t, 'ALERT_UPDATE_SUCCESS', 'success'));
				updateRefresh();
			}
		});
	}

	const editLine = (isAdd?: boolean) =>
		dispatch(
			showOrdersLinesEditDialog({
				baseModel: rowProps.row,
				pricingGroupId: order.ords_pric_id,
				compUid: order.ords_comp_uid,
				discountPercentage: order.disc_percentage,
				isAdd,
				updateRefresh
			})
		);

	if (Utils.getConnectionFromToken() === 'PERCEPTIVE_CONTROL_BEB')
		return [
			<GridActionsCellItem
				label={t('VIEW_ITEM_BUTTON_EDIT')}
				icon={<Icon icon="edit" />}
				onClick={() => editLine(false)}
				disabled={!!rowProps.row.orli_error}
			/>,
			<GridActionsCellItem
				label={t('VIEW_ITEM_BUTTON_DELETE')}
				icon={<Icon icon="delete" />}
				onClick={() => additionalProps?.showDeleteLinesDialog([rowProps.row])}
			/>
		];

	const buttons: MultiButtonOptionType<ViewItemButtonProps>[] = [
		{
			name: 'VIEW_ITEM_BUTTON_EDIT',
			icon: 'edit',
			action: () => editLine(false)
		},
		{
			name: 'VIEW_ITEM_BUTTON_ADD_COPY',
			icon: 'content-copy',
			disabled: () => !!rowProps.row.orli_error,
			action: () => editLine(true)
		},
		{
			name: 'VIEW_ITEM_BUTTON_DELETE',
			icon: 'delete',
			action: () => additionalProps?.showDeleteLinesDialog([rowProps.row])
		}
	];

	return [
		<GridActionsCellItem
			label={t('VIEW_ITEM_BUTTON_MOVE_UP')}
			icon={<Icon icon="arrow-up" />}
			onClick={() => updateOrder(rowProps.row.orli_order - 100)}
			disabled={rowIndex === 0}
		/>,
		<GridActionsCellItem
			label={t('VIEW_ITEM_BUTTON_MOVE_DOWN')}
			icon={<Icon icon="arrow-down" />}
			onClick={() => updateOrder(rowProps.row.orli_order + 100)}
			disabled={rowIndex === maxRows - 1}
		/>,
		ViewTableUtils.renderMenu({
			component: ViewTableRowActionsMenuComponent,
			buttonProps: { settings, updateRefresh },
			items: buttons
		})
	];
}
